import React, { useState, useEffect } from 'react';
import { Shield, Lock, Unlock, AlertTriangle, FileText, Search, Filter, History } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ContractService from '../../services/contractService';
import { useToast } from '../common/Toast';

const LegalHold = () => {
  const { success, error: showError } = useToast();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active-holds'); // 'active-holds' or 'all'
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [holdReason, setHoldReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await ContractService.getContracts({ limit: 100 });
      const list = response.data || [];
      // Normalize legalHold: backend may return active or isOnHold
      const normalized = list.map((c) => ({
        ...c,
        id: c.id || c._id,
        legalHold: c.legalHold
          ? { ...c.legalHold, active: c.legalHold.active === true || c.legalHold.isOnHold === true }
          : undefined
      }));
      setContracts(normalized);
    } catch (err) {
      showError('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = (contract) => {
    setSelectedContract(contract);
    setHoldReason('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedContract) return;

    const isApplying = !selectedContract.legalHold?.active;
    if (isApplying && !holdReason.trim()) {
      showError('Please provide a reason for the Legal Hold');
      return;
    }

    setIsProcessing(true);
    try {
      await ContractService.toggleLegalHold(selectedContract.id, isApplying, holdReason);
      success(isApplying ? 'Legal Hold Applied' : 'Legal Hold Removed');
      setShowModal(false);
      loadData();
    } catch (err) {
      showError(err.response?.data?.message || 'Operation failed');
    } finally {
      setIsProcessing(false);
    }
  };

  // Filter Data based on Tab
  const filteredData = contracts.filter(c => {
    if (activeTab === 'active-holds') return c.legalHold?.active;
    return true; // All contracts
  });

  const columns = [
    {
      key: 'title',
      label: 'Contract',
      render: (text, row) => (
        <div>
          <div className="font-medium text-gray-900">{text}</div>
          <div className="text-xs text-gray-500">{row.contractId}</div>
        </div>
      )
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: '200px'
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => (
        <span className={`badge ${status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>
          {status}
        </span>
      )
    },
    {
      key: 'legalHold',
      label: 'Hold Status',
      width: '150px',
      render: (hold) => hold?.active ? (
        <span className="badge bg-red-100 text-red-800 d-flex align-items-center gap-1">
          <Lock size={12} /> On Hold
        </span>
      ) : (
        <span className="text-gray-400 text-sm">None</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '120px',
      render: (_, row) => {
        const isOnHold = row.legalHold?.active;
        return (
          <button
            onClick={() => handleToggleClick(row)}
            className={`btn btn-sm ${isOnHold ? 'btn-outline-success' : 'btn-outline-danger'}`}
            title={isOnHold ? "Release Hold" : "Apply Hold"}
          >
            {isOnHold ? <Unlock size={16} /> : <Lock size={16} />}
            {isOnHold ? ' Release' : ' Apply'}
          </button>
        );
      }
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Legal Hold Management" 
        description="Lock contracts to prevent modification or deletion during legal disputes or audits."
      />

      <div className="p-6">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 bg-white rounded-t-lg px-4 pt-4">
          <button
            onClick={() => setActiveTab('active-holds')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'active-holds' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Active Holds ({contracts.filter(c => c.legalHold?.active).length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All Contracts
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-b-lg shadow-sm">
          <DataTable 
            data={filteredData}
            columns={columns}
            loading={loading}
            pagination={true}
            searchable={true}
            searchPlaceholder="Search contracts..."
          />
        </div>
      </div>

      {/* Action Modal */}
      {showModal && selectedContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-full ${selectedContract.legalHold?.active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                {selectedContract.legalHold?.active ? <Unlock size={24} /> : <Lock size={24} />}
              </div>
              <h3 className="text-lg font-semibold">
                {selectedContract.legalHold?.active ? 'Release Legal Hold' : 'Apply Legal Hold'}
              </h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {selectedContract.legalHold?.active 
                ? "Releasing this hold will allow users to edit or delete this contract again."
                : "Applying a hold will lock this contract. Users will NOT be able to edit or delete it until the hold is released."}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason {selectedContract.legalHold?.active ? '(Optional)' : '*'}
                </label>
                <textarea
                  className="form-control w-full border-gray-300 rounded-md shadow-sm p-2"
                  rows="3"
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  placeholder={selectedContract.legalHold?.active ? "Reason for release..." : "e.g., Pending litigation Case #123"}
                  required={!selectedContract.legalHold?.active}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                    selectedContract.legalHold?.active 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isProcessing ? 'Processing...' : (selectedContract.legalHold?.active ? 'Release Hold' : 'Apply Hold')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default LegalHold;